/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import { DropdownSimple } from '../Dropdown';
import Icon from '../Icon';
import CalendarIcon from '../../svg/icons/ic_calendar-solid_20x20.svg';
import PeopleIcon from '../../svg/icons/ic_guests_20x20.svg';
import TicketIcon from '../../svg/icons/ic_ticket_20x20.svg';
import { getTranslation } from '../../providers/TranslationProvider';
import { CONSTANTS, NOOP } from './notifyUtils';

const NotifyPreState = (props) => {
  const {
    activeNotifications,
    isGda,
    venueName,
    eventName,
    notifyStart,
    notifyEnd,
    notifyTimeOptions,
    onClickCancelNotify,
    onClickSetNotify,
    selectEnd,
    selectStart,
    selectedDate,
    selectedQuantity,
    serviceTypeId,
  } = props;

  const {
    EVENT_SERVICE_TYPE_IDS, BUTTON_ID_CHANGE, BUTTON_ID_CANCEL, BUTTON_ID_CONFIRM,
  } = CONSTANTS;

  const notifyHeader = getTranslation({
    translationKey: isGda ? 'set_priority_notify' : 'set_notify',
    fallback: isGda ? 'Set Priority Notify' : 'Set Notify',
  });
  const notifyBody = getTranslation({
    translationKey: 'notify_dialog_body',
    fallback: 'If a table opens up, you will receive an email or push notification (if enabled).',
  });
  const detailsServiceTypeId = (activeNotifications) ? parseInt(Object.keys(activeNotifications)[0], 10) : null;
  const isEvent = EVENT_SERVICE_TYPE_IDS.includes(detailsServiceTypeId);
  const detailsLabel = isEvent ? venueName : '';
  const detailsName = isEvent ? eventName : venueName;
  const iconComponent = isEvent ? TicketIcon : PeopleIcon;
  const iconName = isEvent ? 'ticket' : 'people';
  const notifyQuantity = getTranslation({
    translationKey: isEvent ? 'number_tickets' : 'number_guests',
    translationValues: { [isEvent ? 'seats' : 'number']: `${selectedQuantity}` },
    fallback: `${selectedQuantity} ${isEvent ? 'Tickets' : 'Guests'}`,
  });
  const startEndLabel = getTranslation({ translationKey: 'notify_start_end_label', fallback: 'Preferred Start and End Time' });
  const toLabel = getTranslation({ translationKey: 'notify_to', fallback: 'to' });
  const preferredStart = getTranslation({ translationKey: 'notify_preferred_start', fallback: 'Preferred start time' });
  const preferredEnd = getTranslation({ translationKey: 'notify_preferred_end', fallback: 'Preferred end time' });
  const isExistingNotify = activeNotifications && !!activeNotifications[serviceTypeId];
  const notifyRemove = getTranslation({ translationKey: 'remove', fallback: 'Remove' });
  const notifyChangeOrConfirm = getTranslation({
    translationKey: isExistingNotify ? 'change_time' : 'button_confirm',
    fallback: isExistingNotify ? 'Change Time' : 'Confirm',
  });
  const calendarIcon = getTranslation({ translationKey: 'calendar_icon', fallback: 'Calendar icon' });
  const partySizeIcon = getTranslation({ translationKey: 'party_size_icon', fallback: 'Party size icon' });
  const changeOrConfirmVariant = isGda ? 'rga' : 'primary';
  const changeOrConfirmTestId = isExistingNotify ? BUTTON_ID_CHANGE : BUTTON_ID_CONFIRM;
  return (
    <div data-testid="NotifyPreState">
      <div className="Notify__head">
        <h2>{notifyHeader}</h2>
        <p>{notifyBody}</p>
      </div>
      <div className="Notify__venue-details">
        {isEvent && <div className="Notify__event-label">{detailsLabel}</div>}
        <div className="Notify__venue-name">{detailsName}</div>
        <div className="Notify__details-row">
          <Icon component={CalendarIcon} name="calendar" role="img" ariaLabel={calendarIcon} />
          {moment(`${selectedDate}`).format('ddd, ll')}
        </div>
        <div className="Notify__details-row">
          <Icon component={iconComponent} name={iconName} role="img" ariaLabel={partySizeIcon} />
          <span>{notifyQuantity}</span>
        </div>
      </div>
      {notifyStart && notifyEnd && (
        <div className="Notify__body">
          <div className="Notify__prompt">{startEndLabel}</div>
          <div className="Notify__select-times">
            <DropdownSimple
              name="start-time"
              options={notifyTimeOptions}
              onChange={selectStart}
              value={notifyStart}
              ariaLabel={preferredStart}
            />
            <span className="Notify__to">{toLabel}</span>
            <DropdownSimple
              name="end-time"
              options={notifyTimeOptions}
              onChange={selectEnd}
              value={notifyEnd}
              ariaLabel={preferredEnd}
            />
          </div>
        </div>
      )}
      <div className="Notify__cta">
        {isExistingNotify && (
        <>
          <Button
            className="Notify__cancel"
            onClick={onClickCancelNotify}
            data-test-id={BUTTON_ID_CANCEL}
            lg
            variant="cancel"
          >
            {notifyRemove}
          </Button>
        </>
        )}
        <Button
          className="Notify__confirm"
          variant={changeOrConfirmVariant}
          onClick={onClickSetNotify}
          data-test-id={changeOrConfirmTestId}
          lg
        >
          {notifyChangeOrConfirm}
        </Button>
      </div>
    </div>
  );
};

NotifyPreState.propTypes = {
  activeNotifications: PropTypes.shape({
    [PropTypes.number]: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
  }),
  isGda: PropTypes.bool,
  venueName: PropTypes.string,
  eventName: PropTypes.string,
  notifyStart: PropTypes.string,
  notifyEnd: PropTypes.string,
  notifyTimeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  onClickCancelNotify: PropTypes.func,
  onClickSetNotify: PropTypes.func,
  selectEnd: PropTypes.func,
  selectStart: PropTypes.func,
  selectedDate: PropTypes.instanceOf(Date),
  selectedQuantity: PropTypes.number,
  serviceTypeId: PropTypes.number,
};
NotifyPreState.defaultProps = {
  activeNotifications: undefined,
  isGda: undefined,
  venueName: undefined,
  eventName: undefined,
  notifyStart: undefined,
  notifyEnd: undefined,
  notifyTimeOptions: [],
  onClickCancelNotify: NOOP,
  onClickSetNotify: NOOP,
  selectEnd: NOOP,
  selectStart: NOOP,
  selectedDate: undefined,
  selectedQuantity: undefined,
  serviceTypeId: undefined,
};
export default NotifyPreState;
